import createAxios from "./httpCommon";
import { localStorageGetItem, localStorageSetItem } from "../App";

const ANON_KEY = "anonymousId";

// Persistent, browser-tied anonymous id (used when no user is logged in)
export const getAnonymousId = () => {
  let id = localStorageGetItem(ANON_KEY);
  if (!id) {
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `anon-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorageSetItem(ANON_KEY, id);
  }
  return id;
};

const getUserId = () => {
  try {
    const userData = JSON.parse(localStorageGetItem("userData"));
    const token = localStorageGetItem("userToken");
    return userData && token ? userData.id : undefined;
  } catch (e) {
    return undefined;
  }
};

const buildPayload = (
  eventName,
  context,
  { suggestedProductId, rank, quantity },
) => {
  const userId = getUserId();
  return {
    eventName,
    suggestionSetId: context?.suggestionSetId,
    cartSignature: context?.cartSignature,
    ...(userId ? { userId } : {}),
    anonymousId: getAnonymousId(),
    cartProductIds: context?.cartProductIds || [],
    suggestedProductId,
    suggestedProductIds: context?.suggestedProductIds || [],
    rank: typeof rank === "number" ? rank : 0,
    ...(typeof quantity === "number" ? { quantity } : {}),
  };
};

// Best-effort fire-and-forget POST (tracking should never break the UI)
const postEvent = (payload) => {
  createAxios()
    .then((axios) => axios.post("/product/cart-suggestions/events", payload))
    .catch(() => {});
};

// Fired when a suggested product is opened from the cart
export const trackCrossSellViewed = (context, { suggestedProductId, rank }) => {
  if (!context?.suggestionSetId || !suggestedProductId) return;
  postEvent(
    buildPayload("cross_sell_viewed", context, { suggestedProductId, rank }),
  );
};

// Debounced aggregation for cross_sell_added so rapid clicks on the same
// product send ONE event with the combined quantity (not N requests).
const ADDED_DEBOUNCE_MS = 800;
const pendingAdds = new Map(); // suggestedProductId -> { quantity, rank, context, timer }

export const trackCrossSellAdded = (
  context,
  { suggestedProductId, rank, quantity = 1 },
) => {
  if (!context?.suggestionSetId || !suggestedProductId) return;

  const existing = pendingAdds.get(suggestedProductId);
  if (existing?.timer) clearTimeout(existing.timer);

  const timer = setTimeout(() => {
    const entry = pendingAdds.get(suggestedProductId);
    pendingAdds.delete(suggestedProductId);
    if (!entry) return;
    postEvent(
      buildPayload("cross_sell_added", entry.context, {
        suggestedProductId,
        rank: entry.rank,
        quantity: entry.quantity,
      }),
    );
  }, ADDED_DEBOUNCE_MS);

  pendingAdds.set(suggestedProductId, {
    quantity: (existing?.quantity || 0) + quantity,
    rank: rank ?? existing?.rank,
    context, // keep the latest context
    timer,
  });
};
